import React, { useState } from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';

interface NewMessageDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    isGlobalDarkMode: boolean;
}

const NewMessageDialog: React.FC<NewMessageDialogProps> = ({ open, onOpenChange, isGlobalDarkMode }) => {
    const { users, currentUser, createDirectMessage, setActiveDm } = useTeamChat();
    const [search, setSearch] = useState('');

    const filteredUsers = users.filter(u =>
        u.id !== currentUser?.id &&
        (u.displayName.toLowerCase().includes(search.toLowerCase()) ||
            u.name.toLowerCase().includes(search.toLowerCase()))
    );

    const handleSelectUser = async (userId: string) => {
        const dmId = await createDirectMessage(userId);
        if (dmId) {
            setActiveDm(dmId);
            onOpenChange(false);
            setSearch('');
        }
    };

    const textColor = isGlobalDarkMode ? 'text-foreground' : 'text-gray-900';
    const subTextColor = isGlobalDarkMode ? 'text-muted-foreground' : 'text-gray-500';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={cn("sm:max-w-md p-0 overflow-hidden", isGlobalDarkMode ? "bg-background border-border" : "")}>
                <DialogHeader className="p-4 border-b border-border">
                    <DialogTitle className={textColor}>New Message</DialogTitle>
                    <DialogDescription className="sr-only">Start a new private conversation with a workspace member.</DialogDescription>
                </DialogHeader>
                <div className="p-2 border-b border-border">
                    <div className="relative">
                        <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", subTextColor)} />
                        <Input
                            placeholder="Search members..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={cn(
                                "pl-9 border-0 focus-visible:ring-0",
                                isGlobalDarkMode ? "bg-card text-foreground placeholder:text-muted-foreground" : "bg-gray-50"
                            )}
                            autoFocus
                        />
                    </div>
                </div>
                <div className="max-h-[300px] overflow-y-auto p-1">
                    {filteredUsers.length > 0 ? (
                        filteredUsers.map(user => (
                            <button
                                key={user.id}
                                onClick={() => handleSelectUser(user.id)}
                                className={cn(
                                    "w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors text-left",
                                    isGlobalDarkMode ? "hover:bg-card" : "hover:bg-gray-100"
                                )}
                            >
                                <Avatar className="w-8 h-8">
                                    <AvatarImage src={user.avatar} />
                                    <AvatarFallback className={isGlobalDarkMode ? "bg-card text-muted-foreground" : ""}>
                                        {user.displayName.charAt(0)}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col min-w-0">
                                    <span className={cn("text-sm font-medium truncate", textColor)}>
                                        {user.displayName}
                                    </span>
                                    <span className={cn("text-xs truncate", subTextColor)}>
                                        @{user.name}
                                    </span>
                                </div>
                            </button>
                        ))
                    ) : (
                        <div className={cn("p-8 text-center text-sm", subTextColor)}>
                            No members found
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default NewMessageDialog;
